import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  healthReportService,
  HealthReportData,
  mapValueToText
} from '../lib/healthReportService';
import { exportService } from '../lib/exportService';
import { ReportDisclaimer } from '../components/reports/ReportDisclaimer';
import { EmptyState } from '../components/common/EmptyState';
import { Skeleton } from '../components/common/Skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';

export interface HealthReportProps {
  user: any;
  onBack?: () => void;
  /** Callback do nawigacji - np. do sekcji wypróżnień przy braku danych */
  onNavigate?: (section: string) => void;
}

const rem = (px: number) => `${px / 16}rem`;

const fontSize = {
  xs: rem(12),
  sm: rem(14),
  base: rem(16),
  lg: rem(18),
  xl: rem(20),
  '2xl': rem(24)
};

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const sectionCardStyle: React.CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #E5E5E5',
  borderRadius: rem(12),
  padding: rem(24),
  marginBottom: rem(24)
};

const kpiCardStyle: React.CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #E5E5E5',
  borderRadius: rem(12),
  padding: rem(16)
};


const ChartIconEmpty = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10"/>
    <line x1="18" y1="20" x2="18" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="16"/>
  </svg>
);

export const HealthReport = ({ user, onBack, onNavigate }: HealthReportProps) => {
  const reportExportRef = useRef<HTMLDivElement>(null);
  const [report, setReport] = useState<HealthReportData | null>(null);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodFilter, setPeriodFilter] = useState<'14' | '30' | '90' | 'custom'>('30');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');

  const calculateDateRange = useCallback(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (periodFilter === 'custom') {
      return {
        dateFrom: customDateFrom || format(subDays(today, 29), 'yyyy-MM-dd'),
        dateTo: customDateTo || format(today, 'yyyy-MM-dd')
      };
    }
    const days = parseInt(periodFilter);
    const dateFrom = format(subDays(today, days - 1), 'yyyy-MM-dd');
    const dateTo = format(today, 'yyyy-MM-dd');
    return { dateFrom, dateTo };
  }, [periodFilter, customDateFrom, customDateTo]);

  const loadReport = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      const { dateFrom, dateTo } = calculateDateRange();
      const data = await healthReportService.getHealthReport(user.id, dateFrom, dateTo);
      setReport(data);
    } catch (err) {
      console.error('Błąd ładowania raportu zdrowia:', err);
      setError('Nie udało się załadować raportu. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, calculateDateRange]);

  useEffect(() => { loadReport(); }, [loadReport]);

  if (loading) {
    return (
      <div style={{ padding: rem(24) }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(8),
              marginBottom: rem(24),
              padding: `${rem(8)} ${rem(12)}`,
              background: 'transparent',
              border: '1px solid #E5E5E5',
              borderRadius: rem(8),
              color: '#666666',
              fontSize: fontSize.sm,
              cursor: 'pointer',
            }}
          >
            <ArrowLeftIcon />
            Wróć do raportów
          </button>
        )}
        <Skeleton width="100%" height="200px" borderRadius="16px" />
        <div style={{ height: rem(16) }} />
        <Skeleton width="100%" height="120px" borderRadius="16px" />
        <div style={{ height: rem(16) }} />
        <Skeleton width="70%" height="40px" borderRadius="12px" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: rem(24), textAlign: 'center' }}>
        <p style={{ color: '#EF4444', fontSize: fontSize.base, marginBottom: rem(16) }}>{error}</p>
        <button
          onClick={loadReport}
          style={{
            padding: `${rem(10)} ${rem(20)}`,
            background: 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: rem(12),
            fontSize: fontSize.sm,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  const daysOfData = report?.dailyData?.length ?? 0;
  const hasInsufficientData = !report || daysOfData === 0;

  if (hasInsufficientData) {
    return (
      <div style={{ padding: rem(24) }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(8),
              marginBottom: rem(24),
              padding: `${rem(8)} ${rem(12)}`,
              background: 'transparent',
              border: '1px solid #E5E5E5',
              borderRadius: rem(8),
              color: '#666666',
              fontSize: fontSize.sm,
              cursor: 'pointer',
            }}
          >
            <ArrowLeftIcon />
            Wróć do raportów
          </button>
        )}
        <EmptyState
          icon={<ChartIconEmpty />}
          title="Za mało danych do wygenerowania raportu"
          description={`Potrzebujesz co najmniej 14 dni danych aby wygenerować wiarygodny raport. Obecnie masz ${daysOfData} dni.`}
          actionLabel="Dodaj więcej wpisów"
          onAction={() => onNavigate?.('bowel')}
        />
      </div>
    );
  }

  const { dateFrom, dateTo } = calculateDateRange();
  const {
    status,
    bloodDaysLast7,
    trend,
    trendDetails,
    pogorszenieDays,
    triggers,
    medications,
    dataQuality,
    dailyData,
    warnings
  } = report;

  const last7 = dailyData.slice(-7);
  const avgBmLast7 = last7.length > 0 ? last7.reduce((s, d) => s + d.bm_count, 0) / last7.length : 0;
  const avgUrgencyLast7 = last7.filter(d => d.bm_count > 0).length > 0
    ? last7.filter(d => d.bm_count > 0).reduce((s, d) => s + d.avg_urgency, 0) / last7.filter(d => d.bm_count > 0).length
    : 0;
  const avgPainLast7 = last7.filter(d => d.bm_count > 0).length > 0
    ? last7.filter(d => d.bm_count > 0).reduce((s, d) => s + d.avg_pain, 0) / last7.filter(d => d.bm_count > 0).length
    : 0;
  const avgGurglingLast7 = last7.length > 0 ? last7.reduce((s, d) => s + (d.gurgling ?? 0), 0) / last7.length : 0;

  const statusGradient =
    status.color === 'red' ? 'linear-gradient(135deg, #FF4444 0%, #FF6666 100%)'
    : status.color === 'yellow' ? 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)'
    : 'linear-gradient(135deg, #00A86B 0%, #00C896 100%)';

  const isLowDataQuality = dataQuality.assessment === 'za-malo';

  return (
    <div className="report-export-area" style={{ padding: rem(24) }} ref={reportExportRef}>
      <ReportDisclaimer />

      {/* Header – jak Stan choroby */}
      <div style={{ marginBottom: rem(24), display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: rem(8),
                marginBottom: rem(16),
                padding: `${rem(8)} ${rem(12)}`,
                background: 'transparent',
                border: '1px solid #E5E5E5',
                borderRadius: rem(8),
                color: '#666666',
                fontSize: fontSize.sm,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#6B7FFF';
                e.currentTarget.style.color = '#6B7FFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#E5E5E5';
                e.currentTarget.style.color = '#666666';
              }}
            >
              <ArrowLeftIcon />
              <span>Wróć do raportów</span>
            </button>
          )}
          <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(8) }}>
            Raport zdrowia jelit
          </h1>
          <p style={{ fontSize: fontSize.sm, color: '#999999', margin: 0 }}>
            Monitor stanu, wzorce przed pogorszeniem, wpływ leków
          </p>
        </div>
        {report && (
          <button
            className="no-pdf-export"
            onClick={async () => {
              if (!reportExportRef.current || exporting) return;
              setExporting(true);
              try {
                await exportService.exportReportElementToPDF(reportExportRef.current, 'raport_zdrowia_jelit');
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(8),
              padding: `${rem(10)} ${rem(20)}`,
              background: 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: rem(12),
              fontSize: fontSize.sm,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(107, 127, 255, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exporting ? 'Pobieranie…' : 'Pobierz raport'}
          </button>
        )}
      </div>

      {/* Filtry okresu – jak Stan choroby */}
      <div style={{ marginBottom: rem(24), display: 'flex', flexWrap: 'wrap', gap: rem(12), alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: rem(8), background: '#F5F5F5', padding: rem(4), borderRadius: rem(12), border: '1px solid #E5E5E5' }}>
          {(['14', '30', '90'] as const).map(period => (
            <button
              key={period}
              onClick={() => setPeriodFilter(period)}
              style={{
                padding: `${rem(8)} ${rem(16)}`,
                borderRadius: rem(8),
                border: 'none',
                background: periodFilter === period ? '#FFFFFF' : 'transparent',
                color: periodFilter === period ? '#1A1A1A' : '#999999',
                fontSize: fontSize.sm,
                fontWeight: periodFilter === period ? 600 : 400,
                cursor: 'pointer',
                boxShadow: periodFilter === period ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              {period} dni
            </button>
          ))}
          <button
            onClick={() => setPeriodFilter('custom')}
            style={{
              padding: `${rem(8)} ${rem(16)}`,
              borderRadius: rem(8),
              border: 'none',
              background: periodFilter === 'custom' ? '#FFFFFF' : 'transparent',
              color: periodFilter === 'custom' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.sm,
              fontWeight: periodFilter === 'custom' ? 600 : 400,
              cursor: 'pointer',
              boxShadow: periodFilter === 'custom' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
            }}
          >
            Własny zakres
          </button>
        </div>
        {periodFilter === 'custom' && (
          <div style={{ display: 'flex', gap: rem(8), alignItems: 'center' }}>
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
              style={{ padding: `${rem(8)} ${rem(12)}`, borderRadius: rem(8), border: '1px solid #E5E5E5', fontSize: fontSize.sm, color: '#1A1A1A' }}
            />
            <span style={{ color: '#999999' }}>—</span>
            <input
              type="date"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
              style={{ padding: `${rem(8)} ${rem(12)}`, borderRadius: rem(8), border: '1px solid #E5E5E5', fontSize: fontSize.sm, color: '#1A1A1A' }}
            />
          </div>
        )}
        <div style={{ marginLeft: 'auto', fontSize: fontSize.sm, color: '#999999' }}>
          {format(parseISO(dateFrom), 'dd.MM.yyyy', { locale: pl })} — {format(parseISO(dateTo), 'dd.MM.yyyy', { locale: pl })}
        </div>
      </div>

      {warnings.length > 0 && (
        <div style={{ marginBottom: rem(24), padding: rem(16), background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: rem(12) }}>
          {warnings.map((w, i) => (
            <div key={i} style={{ fontSize: fontSize.sm, color: '#92400E', marginBottom: i < warnings.length - 1 ? rem(8) : 0 }}>• {w}</div>
          ))}
        </div>
      )}

      {/* Badge stanu – jak Stan choroby */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: rem(16), marginBottom: rem(24) }}>
        <div style={{
          padding: `${rem(12)} ${rem(20)}`,
          borderRadius: rem(12),
          background: statusGradient,
          color: '#FFFFFF',
          fontSize: fontSize.lg,
          fontWeight: 600,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          {status.status}
        </div>
      </div>

      {/* SEKCJA 1 – MONITOR STANU (wykres jak w Dzisiaj) */}
      <div className="report-card panel" style={sectionCardStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(16) }}>
          Monitor stanu
        </h2>
        <p style={{ fontSize: fontSize.sm, color: '#999999', margin: 0, marginBottom: rem(20) }}>
          Trend w wybranym okresie (wypróżnienia, krew, obciążenie jelitowe)
        </p>

        {/* Wykres – jak w zakładce Dzisiaj (Objawy) */}
        <div style={{
          background: '#F5F5F5',
          borderRadius: rem(12),
          padding: rem(8),
          minHeight: rem(240),
          marginBottom: rem(24)
        }}>
          {dailyData.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: rem(200), color: '#999999', fontSize: fontSize.sm }}>
              Brak danych do wyświetlenia
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart
                data={dailyData.map(d => ({
                  date: format(parseISO(d.date), 'dd.MM', { locale: pl }),
                  fullDate: d.date,
                  wypróżnienia: d.bm_count,
                  krew: d.has_blood ? 1 : null,
                  obciążenie: d.bowelLoad ?? 0
                }))}
                margin={{ top: 5, right: 30, left: -5, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis
                  dataKey="date"
                  stroke="#999999"
                  style={{ fontSize: fontSize.xs }}
                  interval="preserveStartEnd"
                  angle={dailyData.length > 10 ? -45 : 0}
                  textAnchor={dailyData.length > 10 ? 'end' : 'middle'}
                  height={40}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#999999"
                  style={{ fontSize: fontSize.xs }}
                  domain={[0, 'auto']}
                  allowDecimals={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#999999"
                  style={{ fontSize: fontSize.xs }}
                  domain={[0, 100]}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E5E5',
                    borderRadius: rem(8),
                    fontSize: fontSize.xs
                  }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length && payload[0]?.payload) {
                      const p = payload[0].payload;
                      return (
                        <div style={{
                          background: '#FFFFFF',
                          border: '1px solid #E5E5E5',
                          borderRadius: rem(8),
                          padding: rem(12),
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{ fontSize: fontSize.sm, fontWeight: 600, marginBottom: rem(6) }}>{p.fullDate}</div>
                          <div style={{ fontSize: fontSize.xs, color: '#666666' }}>Wypróżnienia: {p.wypróżnienia}</div>
                          <div style={{ fontSize: fontSize.xs, color: '#666666' }}>Krew: {p.krew ? 'tak' : 'nie'}</div>
                          <div style={{ fontSize: fontSize.xs, color: '#666666' }}>Obciążenie: {Math.round(p.obciążenie)} pkt</div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: fontSize.xs, paddingTop: rem(8) }} iconType="line" />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="wypróżnienia"
                  stroke="#6B7FFF"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Wypróżnienia"
                  connectNulls
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="krew"
                  stroke="#FF4444"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Krew"
                  connectNulls
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="obciążenie"
                  stroke="#00A86B"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Obciążenie jelitowe"
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Podsumowanie liczbowe */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: rem(12), marginBottom: rem(20) }}>
          <div className="kpi-card report-card" style={kpiCardStyle}>
            <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>Wypróżnienia (śr.)</div>
            <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A' }}>{avgBmLast7 > 0 ? avgBmLast7.toFixed(1) : '—'}</div>
          </div>
          <div className="kpi-card report-card" style={kpiCardStyle}>
            <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>Krew</div>
            <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A' }}>{bloodDaysLast7} dni</div>
          </div>
          <div style={kpiCardStyle}>
            <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>Pilność</div>
            <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A' }}>
              {avgUrgencyLast7 > 0 ? mapValueToText(avgUrgencyLast7) : '—'}
            </div>
          </div>
          <div className="kpi-card report-card" style={kpiCardStyle}>
            <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>Ból jelit</div>
            <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A' }}>
              {avgPainLast7 > 0 ? mapValueToText(avgPainLast7) : '—'}
            </div>
          </div>
          <div className="kpi-card report-card" style={kpiCardStyle}>
            <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>Przelewanie</div>
            <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A' }}>
              {avgGurglingLast7 > 0 ? mapValueToText(avgGurglingLast7) : '—'}
            </div>
          </div>
        </div>

        <h3 style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(8) }}>
          Porównanie do poprzednich 7 dni
        </h3>
        <p style={{ fontSize: fontSize.sm, color: '#666666', margin: 0, lineHeight: 1.5 }}>
          {trend}
          {trendDetails.length > 0 && trendDetails[0] !== 'Za mało danych do porównania.' && !trendDetails[0].includes('bez istotnych różnic') && (
            <> – {trendDetails.join(', ')}</>
          )}
        </p>
      </div>

      {/* SEKCJA 2 – WZORCE (zawsze widoczna) */}
      <div className="report-card panel" style={sectionCardStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(16) }}>
          Co działo się przed pogorszeniem
        </h2>
        <p style={{ fontSize: fontSize.sm, color: '#999999', margin: 0, marginBottom: rem(20) }}>
          Analiza {dailyData.length} dni
        </p>
        {dailyData.length < 14 ? (
          <p style={{ fontSize: fontSize.sm, color: '#666666' }}>Za mało dni do analizy wzorców (potrzeba min. 14 dni)</p>
        ) : pogorszenieDays.length < 5 ? (
          <p style={{ fontSize: fontSize.sm, color: '#666666' }}>Za mało przypadków pogorszenia do analizy wzorców</p>
        ) : (
          <>
            <p style={{ fontSize: fontSize.sm, color: '#666666', marginBottom: rem(16) }}>
              Znalazłam {pogorszenieDays.length} dni z pogorszeniem. Oto co występowało 1–3 dni wcześniej:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: rem(24) }}>
              <div>
                <h3 style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(12) }}>Objawy zapowiadające</h3>
                <ul style={{ margin: 0, paddingLeft: rem(20), fontSize: fontSize.sm, color: '#666666', lineHeight: 1.8 }}>
                  {triggers.objawy.filter(t => t.count > 0).map(t => (
                    <li key={t.key}>{t.label}: w {t.count} na {t.total} przypadków</li>
                  ))}
                  {triggers.objawy.filter(t => t.count > 0).length === 0 && <li>Brak danych</li>}
                </ul>
              </div>
              <div>
                <h3 style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(12) }}>Czynniki regulujące</h3>
                <ul style={{ margin: 0, paddingLeft: rem(20), fontSize: fontSize.sm, color: '#666666', lineHeight: 1.8 }}>
                  {triggers.czynniki.filter(t => t.count > 0).map(t => (
                    <li key={t.key}>{t.label}: w {t.count} na {t.total} przypadków</li>
                  ))}
                  {triggers.czynniki.filter(t => t.count > 0).length === 0 && <li>Brak danych</li>}
                </ul>
              </div>
            </div>
          </>
        )}
      </div>

      {/* SEKCJA 3 – LEKI (zawsze widoczna) */}
      <div style={sectionCardStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(16) }}>
          Leki
        </h2>
        {medications.length === 0 ? (
          <p style={{ fontSize: fontSize.sm, color: '#666666' }}>Brak aktywnych leków do analizy</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: rem(24) }}>
            {medications.map(m => (
              <div key={m.medication.id} className="medication-card report-card" style={{ padding: rem(16), background: '#F9F9F9', borderRadius: rem(8), border: '1px solid #E5E5E5' }}>
                <h3 style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(8) }}>
                  {m.medication.name}
                </h3>
                <p style={{ fontSize: fontSize.xs, color: '#999999', margin: 0, marginBottom: rem(16) }}>
                  Rozpoczęta: {format(parseISO(m.medication.startDate), 'dd.MM.yyyy', { locale: pl })}
                </p>
                {!m.hasEnoughBefore ? (
                  <p style={{ fontSize: fontSize.sm, color: '#666666' }}>Za mało danych sprzed rozpoczęcia leku. Nie mogę porównać przed/po.</p>
                ) : (
                  <>
                    <div style={{ marginBottom: rem(12) }}>
                      <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>Przed rozpoczęciem (14 dni)</div>
                      <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                        Wypróżnienia: średnio {m.before14Days.avgBm.toFixed(1)} dziennie
                      </div>
                      <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                        Dni z krwią: {m.before14Days.bloodDays} na 14 dni
                      </div>
                      <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                        Średnie obciążenie jelitowe: {Math.round(m.before14Days.avgLoad)} pkt
                      </div>
                    </div>
                    <div style={{ marginBottom: rem(12) }}>
                      <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>Po rozpoczęciu (30 dni)</div>
                      <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                        Wypróżnienia: średnio {m.after30Days.avgBm.toFixed(1)} dziennie
                      </div>
                      <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                        Dni z krwią: {m.after30Days.bloodDays} na 30 dni
                      </div>
                      <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                        Średnie obciążenie jelitowe: {Math.round(m.after30Days.avgLoad)} pkt
                      </div>
                    </div>
                    {!isLowDataQuality ? (
                      <p style={{ fontSize: fontSize.sm, fontWeight: 600, color: '#1A1A1A' }}>
                        Zmiana:{' '}
                        {m.summary === 'poprawa' && 'wyraźna poprawa po rozpoczęciu leku.'}
                        {m.summary === 'pogorszenie' && 'pogorszenie po rozpoczęciu – skonsultuj z lekarzem.'}
                        {m.summary === 'bez-wielkich-zmian' && 'bez wyraźnych zmian.'}
                      </p>
                    ) : (
                      <p style={{ fontSize: fontSize.sm, color: '#666666' }}>
                        Za mało danych do formułowania wniosków. Powyżej tylko porównanie liczb.
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SEKCJA 4 – JAKOŚĆ DANYCH (zawsze widoczna) */}
      <div className="report-card panel" style={sectionCardStyle}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: '#1A1A1A', margin: 0, marginBottom: rem(16) }}>
          Jakość danych
        </h2>
        <p style={{ fontSize: fontSize.sm, color: '#999999', margin: 0, marginBottom: rem(16) }}>
          Okres analizy: {dataQuality.period}
        </p>
        <div style={{ display: 'grid', gap: rem(8), marginBottom: rem(16) }}>
          <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
            Wypróżnienia: {dataQuality.bmDays} na {dataQuality.totalDays} dni ({dataQuality.bmCoverage}%)
          </div>
          <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
            Samopoczucie: {dataQuality.trackingDays} na {dataQuality.totalDays} dni ({dataQuality.trackingCoverage}%)
          </div>
        </div>
        <p style={{ fontSize: fontSize.base, fontWeight: 600, color: '#1A1A1A' }}>
          Ocena: {dataQuality.assessmentText}
        </p>
      </div>
    </div>
  );
};
